import React, { useState, useEffect, useRef } from 'react'
import './Kontakt.scss'

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    courseType: '',
    message: ''
  })
  const [focusedField, setFocusedField] = useState('')
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessNotification, setShowSuccessNotification] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef(null)

  const courseOptions = [
    { value: '', label: '' },
    { value: 'zaklad', label: 'Základní kurz - 21.900 Kč' },
    { value: 'expresni', label: 'Expresní Solitér - 30.900 Kč' },
    { value: 'suv', label: 'SUV kurz - 25.500 Kč' },
    { value: 'kondicni', label: 'Kondičné jízdy' },
    { value: 'jine', label: 'Jiné dotazy' }
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.2 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Jméno je povinné'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email je povinný'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email není ve správném formátu'
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefon je povinný'
    } else if (!/^(\+420)?[0-9\s]{9,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Telefon není ve správném formátu'
    }

    if (!formData.courseType) {
      newErrors.courseType = 'Vyberte typ kurzu'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&")
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError('')
    
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "kontakt",
          ...formData
        })
      })
      
      // Zobrazit úspěšnou notifikaci
      setShowSuccessNotification(true)
      
      // Vyčistit formulář
      setFormData({
        name: '',
        email: '',
        phone: '',
        courseType: '',
        message: ''
      })
      
      // Skrýt notifikaci po 5 sekundách
      setTimeout(() => {
        setShowSuccessNotification(false)
      }, 500000)
      
    } catch (error) {
      console.error('Error submitting form:', error)
      setSubmitError('Došlo k chybě při odeslání formuláře. Zkuste to prosím znovu nebo nás kontaktujte telefonicky.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseNotification = () => {
    setShowSuccessNotification(false)
  }

  const contactInfo = [
    {
      icon: '📍',
      title: 'Adresa',
      content: 'Učebna: Husova 370, 697 01 Kyjov',
      subtitle: 'Sídlo: Školní 277, 696 02 Ratíškovice'
    },
    {
      icon: '📞',
      title: 'Telefon',
      content: '728 108 824',
      subtitle: 'Volejte kdykoliv',
      action: 'tel:728108824'
    },
    {
      icon: '📘',
      title: 'Facebook',
      content: 'Autoškola Příkaský',
      subtitle: 'Aktuální informace o kurzech',
      action: 'https://www.facebook.com/p/Auto%C5%A1kola-P%C5%99%C3%ADkask%C3%BD-Rat%C3%AD%C5%A1kovice-Kyjov-a-Hodon%C3%ADn-100051923246952/?locale=cs_CZ',
      external: true
    },
    {
      icon: '✉️',
      title: 'Email',
      content: 'josef.prikasky@seznam.cz',
      subtitle: 'Odpovídáme do 48 hodin',
      action: 'mailto:josef.prikasky@seznam.cz'
    },
    {
      icon: '⏰',
      title: 'Otevírací doba',
      content: 'Po-Pá: 8:00-18:00',
      subtitle: 'Víkendy dle domluvy'
    }
  ]

  return (
    <div className={`contact-section ${isVisible ? 'visible' : ''}`} ref={sectionRef}>
      {/* Úspěšná notifikace */}
      {showSuccessNotification && (
        <div className="success-notification">
          <div className="notification-content">
            <div className="notification-icon">✅</div>
            <div className="notification-text">
              <h4>Děkujeme za váš zájem!</h4>
              <p>Vaše zpráva byla úspěšně odeslána. Ozveme se Vám do 24 hodin.</p>
            </div>
            <button 
              className="notification-close" 
              onClick={handleCloseNotification}
              aria-label="Zavřít notifikaci"
            >
              ✓ Rozumím
            </button>
          </div>
        </div>
      )}

      <div className="section-content">
        <div className="contact-header">
          <h2>Kontaktujte nás</h2>
          <p>Máte dotazy? Rádi Vám pomůžeme a najdeme nejlepší řešení pro vás</p>
        </div>

        <div className="contact-grid">
          <div className="contact-form-container">
            <form 
              className="contact-form" 
              onSubmit={handleSubmit}
              name="kontakt"
              method="post"
              netlify
            >
              {/* Netlify form detection */}
              <input type="hidden" name="form-name" value="kontakt" />
              
              {/* Honeypot field for spam protection */}
              <div style={{ display: 'none' }}>
                <input name="bot-field" />
              </div>

              <div className="form-header">
                <h3>Nezávazná poptávka</h3>
                <p>Vyplňte formulář a my se Vám ozveme</p>
              </div>

              {submitError && (
                <div className="error-banner">
                  <span className="error-icon">⚠️</span>
                  <span>{submitError}</span>
                </div>
              )}

              <div className="form-fields">
                <div className={`form-field ${focusedField === 'name' ? 'focused' : ''} ${formData.name ? 'filled' : ''} ${errors.name ? 'error' : ''}`}>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField('')}
                    required
                  />
                  <label htmlFor="name">Jméno a příjmení</label>
                  <div className="field-border"></div>
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className={`form-field ${focusedField === 'email' ? 'focused' : ''} ${formData.email ? 'filled' : ''} ${errors.email ? 'error' : ''}`}>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField('')}
                    required
                  />
                  <label htmlFor="email">Emailová adresa</label>
                  <div className="field-border"></div>
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className={`form-field ${focusedField === 'phone' ? 'focused' : ''} ${formData.phone ? 'filled' : ''} ${errors.phone ? 'error' : ''}`}>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField('')}
                    required
                  />
                  <label htmlFor="phone">Telefonní číslo</label>
                  <div className="field-border"></div>
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <div className={`form-field select-field ${focusedField === 'courseType' ? 'focused' : ''} ${formData.courseType ? 'filled' : ''} ${errors.courseType ? 'error' : ''}`}>
                  <select
                    id="courseType"
                    name="courseType"
                    value={formData.courseType}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('courseType')}
                    onBlur={() => setFocusedField('')}
                    required
                  >
                    {courseOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="courseType">Typ kurzu</label>
                  <div className="field-border"></div>
                  {errors.courseType && <span className="error-message">{errors.courseType}</span>}
                </div>

                <div className={`form-field textarea-field ${focusedField === 'message' ? 'focused' : ''} ${formData.message ? 'filled' : ''}`}>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField('')}
                    rows="4"
                  />
                  <label htmlFor="message">Zpráva (nepovinné)</label>
                  <div className="field-border"></div>
                </div>
              </div>

              <button 
                type="submit" 
                className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                <span className="btn-text">
                  {isSubmitting ? 'Odesílám...' : 'Odeslat poptávku'}
                </span>
                <div className="btn-loading">
                  <div className="loading-spinner"></div>
                </div>
                <div className="btn-success">✓</div>
              </button>
            </form>
          </div>

          <div className="contact-info">
            <div className="info-cards">
              {contactInfo.map((info, index) => (
                <div key={index} className="info-card" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card-icon">{info.icon}</div>
                  <div className="card-content">
                    <h4>{info.title}</h4>
                    {info.action ? (
                      <a 
                        href={info.action} 
                        target={info.external ? "_blank" : undefined}
                        rel={info.external ? "noopener noreferrer" : undefined}
                        className="contact-link"
                      >
                        {info.content}
                        {info.external && <span className="external-icon">↗</span>}
                      </a>
                    ) : (
                      <span className="contact-text">{info.content}</span>
                    )}
                    <p className="card-subtitle">{info.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="quick-contact">
              <h4>Rychlý kontakt</h4>
              <p>Pro okamžitou pomoc nás můžete kontaktovat přímo:</p>
              <div className="quick-buttons">
                <a href="tel:728108824" className="quick-btn call">
                  <span className="btn-icon">📞</span>
                  <span>Zavolat</span>
                </a>
                <a href="mailto:josef.prikasky@seznam.cz" className="quick-btn email">
                  <span className="btn-icon">✉️</span>
                  <span>Email</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="contact-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>
    </div>
  )
}

export default ContactSection